import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectIsAdmin } from '../store/userSlice';
import { OpenAPI } from '../api/core/OpenAPI';
import { request as __request } from '../api/core/request';
import { toast } from 'react-toastify';
import './AdminAccess.css';

function AdminAccess() {
  const isAdmin = useSelector(selectIsAdmin);
  const [tab, setTab] = useState('stats');

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-card">
          <p style={{ textAlign: 'center', color: '#c62828' }}>Brak uprawnień administratora</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-card">
        <h1 className="admin-title">Panel administracyjny</h1>
        <div className="admin-tabs">
          <button className={`admin-tab ${tab === 'stats' ? 'admin-tab--active' : ''}`} onClick={() => setTab('stats')}>Dashboard</button>
          <button className={`admin-tab ${tab === 'access' ? 'admin-tab--active' : ''}`} onClick={() => setTab('access')}>Dostępy</button>
          <button className={`admin-tab ${tab === 'users' ? 'admin-tab--active' : ''}`} onClick={() => setTab('users')}>Użytkownicy</button>
          <button className={`admin-tab ${tab === 'clubs' ? 'admin-tab--active' : ''}`} onClick={() => setTab('clubs')}>Kluby</button>
          <button className={`admin-tab ${tab === 'seasons' ? 'admin-tab--active' : ''}`} onClick={() => setTab('seasons')}>Sezony</button>
          <button className={`admin-tab ${tab === 'subs' ? 'admin-tab--active' : ''}`} onClick={() => setTab('subs')}>Subskrypcje</button>
          <button className={`admin-tab ${tab === 'games' ? 'admin-tab--active' : ''}`} onClick={() => setTab('games')}>Sparingi</button>
          <button className={`admin-tab ${tab === 'invitations' ? 'admin-tab--active' : ''}`} onClick={() => setTab('invitations')}>Zaproszenia</button>
          <button className={`admin-tab ${tab === 'emails' ? 'admin-tab--active' : ''}`} onClick={() => setTab('emails')}>Emaile</button>
        </div>

        {tab === 'stats' && <StatsTab />}
        {tab === 'access' && <AccessTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'clubs' && <ClubsTab />}
        {tab === 'seasons' && <SeasonsTab />}
        {tab === 'subs' && <SubscriptionsTab />}
        {tab === 'games' && <GamesTab />}
        {tab === 'invitations' && <InvitationsTab />}
        {tab === 'emails' && <EmailsTab />}
      </div>
    </div>
  );
}

// ========== Dashboard ==========
function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const result = await __request(OpenAPI, { method: 'GET', url: '/admin/stats' });
        setStats(result);
      } catch { toast.error('Nie udało się pobrać statystyk'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="admin-loading">Ładowanie...</p>;
  if (!stats) return null;

  return (
    <div className="admin-stats-grid">
      <div className="admin-stat-card">
        <span className="admin-stat-value">{stats.totalSportsClubs}</span>
        <span className="admin-stat-label">Kluby sportowe</span>
      </div>
      <div className="admin-stat-card">
        <span className="admin-stat-value">{stats.totalTeams}</span>
        <span className="admin-stat-label">Drużyny</span>
      </div>
      <div className="admin-stat-card">
        <span className="admin-stat-value">{stats.totalGames}</span>
        <span className="admin-stat-label">Sparingi (łącznie)</span>
      </div>
      <div className="admin-stat-card">
        <span className="admin-stat-value">{stats.pendingGames}</span>
        <span className="admin-stat-label">Oczekujące</span>
      </div>
      <div className="admin-stat-card">
        <span className="admin-stat-value">{stats.approvedGames}</span>
        <span className="admin-stat-label">Zatwierdzone</span>
      </div>
      <div className="admin-stat-card">
        <span className="admin-stat-value">{stats.activeSubscriptions}</span>
        <span className="admin-stat-label">Aktywne subskrypcje</span>
      </div>
      <div className="admin-stat-card">
        <span className="admin-stat-value">{stats.teamsWithFullAccess}</span>
        <span className="admin-stat-label">Pełny dostęp</span>
      </div>
    </div>
  );
}

// ========== Dostępy ==========
function AccessTab() {
  const [query, setQuery] = useState('');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    setLoading(true); setSearched(true);
    try {
      const result = await __request(OpenAPI, { method: 'GET', url: '/admin/teams', query: { query: query || undefined } });
      setTeams(result);
    } catch { toast.error('Nie udało się pobrać drużyn'); }
    finally { setLoading(false); }
  };

  const handleToggle = async (teamId, currentValue) => {
    try {
      const updated = await __request(OpenAPI, { method: 'PUT', url: '/admin/teams/{teamId}/full-access', path: { teamId }, query: { enabled: !currentValue } });
      setTeams(prev => prev.map(t => t.id === teamId ? updated : t));
      toast.success(!currentValue ? 'Pełny dostęp nadany' : 'Pełny dostęp odebrany');
    } catch { toast.error('Nie udało się zmienić dostępu'); }
  };

  return (
    <>
      <div className="admin-search-bar">
        <input type="text" className="admin-search-input" placeholder="Szukaj drużyny po nazwie..."
          value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
        <button className="admin-search-btn" onClick={handleSearch} disabled={loading}>{loading ? 'Szukam...' : 'Szukaj'}</button>
      </div>
      {searched && !loading && teams.length === 0 && <p className="admin-no-results">Brak wyników</p>}
      {teams.length > 0 && (
        <div className="admin-teams-list">
          {teams.map(team => (
            <div key={team.id} className={`admin-team-item ${team.hasFullAccess ? 'admin-team-item--active' : ''}`}>
              <div className="admin-team-info">
                <span className="admin-team-name">{team.name}</span>
                <span className="admin-team-meta">
                  {team.sportsClubName}{team.tier && <> &middot; {team.tier}</>}
                </span>
              </div>
              <button className={`admin-toggle-btn ${team.hasFullAccess ? 'admin-toggle-btn--active' : ''}`}
                onClick={() => handleToggle(team.id, team.hasFullAccess)}>
                {team.hasFullAccess ? 'Pełny dostęp' : 'Darmowy'}
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ========== Użytkownicy ==========
function UsersTab() {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    setLoading(true); setSearched(true);
    try {
      const result = await __request(OpenAPI, { method: 'GET', url: '/admin/users', query: { query: query || undefined } });
      setUsers(result);
    } catch { toast.error('Nie udało się pobrać użytkowników'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div className="admin-search-bar">
        <input type="text" className="admin-search-input" placeholder="Szukaj po emailu..."
          value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
        <button className="admin-search-btn" onClick={handleSearch} disabled={loading}>{loading ? 'Szukam...' : 'Szukaj'}</button>
      </div>
      {searched && !loading && users.length === 0 && <p className="admin-no-results">Brak wyników</p>}
      {users.length > 0 && (
        <div className="admin-teams-list">
          {users.map(user => (
            <div key={user.id} className="admin-team-item">
              <div className="admin-team-info">
                <span className="admin-team-name">{user.displayName}</span>
                <span className="admin-team-meta">
                  {user.username} &middot; {user.userType === 'SPORTS_CLUB' ? 'Klub' : 'Trener'}
                  {user.voivodeship && <> &middot; {user.voivodeship}</>}
                  {user.county && <>, {user.county}</>}
                  &middot; {user.teamCount} drużyn
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ========== Kluby (zatwierdzanie) ==========
function ClubsTab() {
  const [query, setQuery] = useState('');
  const [approvedFilter, setApprovedFilter] = useState('false');
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Activation code lookup
  const [activationCode, setActivationCode] = useState('');
  const [activationResult, setActivationResult] = useState(null);
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationError, setActivationError] = useState('');

  const fetchClubs = async (p = 0) => {
    setLoading(true);
    try {
      const params = { approved: approvedFilter === 'true', page: p, size: 20 };
      if (query) params.query = query;
      const result = await __request(OpenAPI, { method: 'GET', url: '/admin/sports-clubs', query: params });
      setClubs(result.content || []);
      setTotalPages(result.totalPages || 0);
      setTotalElements(result.totalElements || 0);
      setPage(result.currentPage || 0);
    } catch { toast.error('Nie udało się pobrać klubów'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchClubs(0);
    setInitialLoaded(true);
  }, []);

  useEffect(() => {
    if (initialLoaded) fetchClubs(0);
  }, [approvedFilter]);

  const handleSearch = () => fetchClubs(0);

  const handleToggle = async (id, currentApproved) => {
    try {
      const updated = await __request(OpenAPI, { method: 'PUT', url: '/admin/sports-clubs/{id}/approve', path: { id }, query: { approved: !currentApproved } });
      setClubs(prev => prev.map(c => c.id === id ? updated : c));
      toast.success(!currentApproved ? 'Klub zatwierdzony' : 'Zatwierdzenie cofnięte');
    } catch { toast.error('Nie udało się zmienić statusu'); }
  };

  const handleToggleBlock = async (id, currentBlocked) => {
    try {
      const updated = await __request(OpenAPI, { method: 'PUT', url: '/admin/sports-clubs/{id}/block', path: { id }, query: { blocked: !currentBlocked } });
      setClubs(prev => prev.map(c => c.id === id ? updated : c));
      toast.success(!currentBlocked ? 'Klub zablokowany (wraz z trenerami)' : 'Klub odblokowany (wraz z trenerami)');
    } catch { toast.error('Nie udało się zmienić statusu blokady'); }
  };

  const handleActivationCodeSearch = async () => {
    if (!activationCode.trim()) return;
    setActivationLoading(true);
    setActivationError('');
    setActivationResult(null);
    try {
      const result = await __request(OpenAPI, { method: 'GET', url: '/admin/sports-clubs/by-activation-code', query: { code: activationCode.trim() } });
      setActivationResult(result);
    } catch (err) {
      setActivationError(err?.body?.message || 'Nie znaleziono klubu z podanym kodem aktywacyjnym');
    } finally { setActivationLoading(false); }
  };

  const handleActivationToggle = async (id, currentApproved) => {
    try {
      const updated = await __request(OpenAPI, { method: 'PUT', url: '/admin/sports-clubs/{id}/approve', path: { id }, query: { approved: !currentApproved } });
      setActivationResult(updated);
      toast.success(!currentApproved ? 'Klub zatwierdzony' : 'Zatwierdzenie cofnięte');
    } catch { toast.error('Nie udało się zmienić statusu'); }
  };

  return (
    <>
      {/* Weryfikacja kodem aktywacyjnym */}
      <div className="admin-section-label">Weryfikacja kodem aktywacyjnym</div>
      <div className="admin-search-bar">
        <input type="text" className="admin-search-input" placeholder="Wklej kod aktywacyjny (UUID)..."
          value={activationCode} onChange={(e) => setActivationCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleActivationCodeSearch()} />
        <button className="admin-search-btn" onClick={handleActivationCodeSearch}
          disabled={activationLoading || !activationCode.trim()}>
          {activationLoading ? 'Szukam...' : 'Sprawdź'}
        </button>
      </div>

      {activationError && <p className="admin-activation-error">{activationError}</p>}

      {activationResult && (
        <div className={`admin-team-item ${activationResult.approved ? 'admin-team-item--active' : 'admin-team-item--pending'}`}
          style={{ marginBottom: '24px' }}>
          <div className="admin-team-info">
            <span className="admin-team-name">{activationResult.name}</span>
            <span className="admin-team-meta">
              {activationResult.username} {activationResult.regon && <>&middot; REGON: {activationResult.regon}</>}
              {activationResult.voivodeship && <> &middot; {activationResult.voivodeship}</>}
              {activationResult.county && <>, {activationResult.county}</>}
            </span>
          </div>
          <div className="admin-club-actions">
            <button className={`admin-toggle-btn ${activationResult.approved ? 'admin-toggle-btn--active' : 'admin-toggle-btn--warning'}`}
              onClick={() => handleActivationToggle(activationResult.id, activationResult.approved)}>
              {activationResult.approved ? 'Zatwierdzony' : 'Niezatwierdzony'}
            </button>
          </div>
        </div>
      )}

      {/* Lista klubów */}
      <div className="admin-section-label">Lista klubów</div>
      <div className="admin-search-bar">
        <input type="text" className="admin-search-input" placeholder="Szukaj po nazwie klubu..."
          value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
        <select className="admin-status-select" value={approvedFilter} onChange={(e) => setApprovedFilter(e.target.value)}>
          <option value="false">Niezatwierdzone</option>
          <option value="true">Zatwierdzone</option>
        </select>
        <button className="admin-search-btn" onClick={handleSearch} disabled={loading}>{loading ? 'Szukam...' : 'Szukaj'}</button>
      </div>

      {!loading && clubs.length === 0 && <p className="admin-no-results">Brak wyników</p>}

      {clubs.length > 0 && (
        <>
          <p className="admin-results-count">Znaleziono: {totalElements}</p>
          <div className="admin-teams-list">
            {clubs.map(club => (
              <div key={club.id} className={`admin-team-item ${club.approved ? 'admin-team-item--active' : 'admin-team-item--pending'}`}>
                <div className="admin-team-info">
                  <span className="admin-team-name">{club.name}</span>
                  <span className="admin-team-meta">
                    {club.username} {club.regon && <>&middot; REGON: {club.regon}</>}
                    {club.voivodeship && <> &middot; {club.voivodeship}</>}
                    {club.county && <>, {club.county}</>}
                  </span>
                </div>
                <div className="admin-club-actions">
                  <button className={`admin-toggle-btn ${club.approved ? 'admin-toggle-btn--active' : 'admin-toggle-btn--warning'}`}
                    onClick={() => handleToggle(club.id, club.approved)}>
                    {club.approved ? 'Zatwierdzony' : 'Niezatwierdzony'}
                  </button>
                  <button className={`admin-toggle-btn ${club.blocked ? 'admin-toggle-btn--danger' : 'admin-toggle-btn--neutral'}`}
                    onClick={() => handleToggleBlock(club.id, club.blocked)}>
                    {club.blocked ? 'Zablokowany' : 'Aktywny'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="admin-pagination">
              <button className="admin-page-btn" disabled={page === 0} onClick={() => fetchClubs(page - 1)}>Poprzednia</button>
              <span className="admin-page-info">Strona {page + 1} z {totalPages}</span>
              <button className="admin-page-btn" disabled={page >= totalPages - 1} onClick={() => fetchClubs(page + 1)}>Następna</button>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ========== Sezony ==========
function SeasonsTab() {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', minStartDate: '', maxEndDate: '', type: 'PRESEASON', visible: false });

  const fetchSeasons = async () => {
    try {
      const result = await __request(OpenAPI, { method: 'GET', url: '/admin/seasons' });
      setSeasons(result);
    } catch { toast.error('Nie udało się pobrać sezonów'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSeasons(); }, []);

  const handleSave = async () => {
    try {
      if (editId) {
        await __request(OpenAPI, { method: 'PUT', url: '/admin/seasons/{id}', path: { id: editId }, body: form, mediaType: 'application/json' });
        toast.success('Sezon zaktualizowany');
      } else {
        await __request(OpenAPI, { method: 'POST', url: '/admin/seasons', body: form, mediaType: 'application/json' });
        toast.success('Sezon utworzony');
      }
      setForm({ name: '', minStartDate: '', maxEndDate: '', type: 'PRESEASON', visible: false });
      setEditId(null);
      fetchSeasons();
    } catch { toast.error('Nie udało się zapisać sezonu'); }
  };

  const handleEdit = (season) => {
    setEditId(season.id);
    setForm({ name: season.name, minStartDate: season.minStartDate, maxEndDate: season.maxEndDate, type: season.type, visible: season.visible });
  };

  const handleCancel = () => {
    setEditId(null);
    setForm({ name: '', minStartDate: '', maxEndDate: '', type: 'PRESEASON', visible: false });
  };

  if (loading) return <p className="admin-loading">Ładowanie...</p>;

  return (
    <>
      <div className="admin-season-form">
        <h3 className="admin-section-title">{editId ? 'Edytuj sezon' : 'Nowy sezon'}</h3>
        <div className="admin-season-fields">
          <input type="text" className="admin-search-input" placeholder="Nazwa sezonu"
            value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} />
          <input type="date" className="admin-search-input" value={form.minStartDate}
            onChange={(e) => setForm(prev => ({ ...prev, minStartDate: e.target.value }))} />
          <input type="date" className="admin-search-input" value={form.maxEndDate}
            onChange={(e) => setForm(prev => ({ ...prev, maxEndDate: e.target.value }))} />
          <select className="admin-search-input" value={form.type} onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}>
            <option value="PRESEASON">Okres przygotowawczy</option>
            <option value="YEAR_ROUND">Całoroczny</option>
          </select>
          <label className="admin-checkbox-label">
            <input type="checkbox" checked={form.visible} onChange={(e) => setForm(prev => ({ ...prev, visible: e.target.checked }))} />
            <span>Widoczny dla użytkowników</span>
          </label>
        </div>
        <div className="admin-season-actions">
          <button className="admin-search-btn" onClick={handleSave}
            disabled={!form.name || !form.minStartDate || !form.maxEndDate}>
            {editId ? 'Zapisz zmiany' : 'Utwórz sezon'}
          </button>
          {editId && <button className="admin-cancel-btn" onClick={handleCancel}>Anuluj</button>}
        </div>
      </div>

      <div className="admin-teams-list" style={{ marginTop: '1rem' }}>
        {seasons.length === 0 && <p className="admin-no-results">Brak sezonów</p>}
        {seasons.map(season => (
          <div key={season.id} className="admin-team-item">
            <div className="admin-team-info">
              <span className="admin-team-name">{season.name}</span>
              <span className="admin-team-meta">
                {season.minStartDate} — {season.maxEndDate} &middot; {season.type === 'YEAR_ROUND' ? 'Całoroczny' : 'Okres przygotowawczy'}
              </span>
            </div>
            <div className="admin-game-actions">
              <span className={`admin-badge ${season.visible ? 'admin-badge--active' : 'admin-badge--expired'}`}>
                {season.visible ? 'Widoczny' : 'Ukryty'}
              </span>
              <button className="admin-toggle-btn" onClick={() => handleEdit(season)}>Edytuj</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ========== Subskrypcje ==========
function SubscriptionsTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const result = await __request(OpenAPI, { method: 'GET', url: '/admin/subscriptions' });
        setSubs(result);
      } catch { toast.error('Nie udało się pobrać subskrypcji'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="admin-loading">Ładowanie...</p>;

  return (
    <div className="admin-teams-list">
      {subs.length === 0 && <p className="admin-no-results">Brak subskrypcji</p>}
      {subs.map(sub => (
        <div key={sub.id} className={`admin-team-item ${sub.active ? 'admin-team-item--active' : ''}`}>
          <div className="admin-team-info">
            <span className="admin-team-name">{sub.sportsClubName}</span>
            <span className="admin-team-meta">
              {sub.teamName && <>{sub.teamName} &middot; </>}
              {sub.subscriptionName} &middot; {sub.startDate} — {sub.endDate}
            </span>
          </div>
          <span className={`admin-badge ${sub.active ? 'admin-badge--active' : 'admin-badge--expired'}`}>
            {sub.active ? 'Aktywna' : 'Wygasła'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ========== Sparingi ==========
function GamesTab() {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  const statuses = [
    { value: '', label: 'Wszystkie' },
    { value: 'PENDING', label: 'Oczekujące' },
    { value: 'PENDING_DATE_TIME_PROPOSAL', label: 'Propozycja daty' },
    { value: 'PENDING_CHANGE_DATE', label: 'Zmiana daty' },
    { value: 'APPROVED', label: 'Zatwierdzone' },
    { value: 'APPROVED_WITH_TIME_TO_CONFIRM', label: 'Do potwierdzenia' },
    { value: 'REJECTED', label: 'Odrzucone' },
    { value: 'CANCELLED', label: 'Anulowane' },
    { value: 'PENDING_LAST_MINUTE', label: 'Last minute' },
  ];

  const statusLabels = Object.fromEntries(statuses.map(s => [s.value, s.label]));

  const handleSearch = async () => {
    setLoading(true); setSearched(true);
    try {
      const params = {};
      if (query) params.query = query;
      if (statusFilter) params.status = statusFilter;
      const result = await __request(OpenAPI, { method: 'GET', url: '/admin/games', query: params });
      setGames(result);
    } catch { toast.error('Nie udało się pobrać sparingów'); }
    finally { setLoading(false); }
  };

  const handleCancel = async (gameId) => {
    if (!window.confirm('Czy na pewno chcesz anulować ten sparing? Obie drużyny zostaną powiadomione.')) return;
    setCancellingId(gameId);
    try {
      const updated = await __request(OpenAPI, { method: 'PUT', url: '/admin/games/{id}/cancel', path: { id: gameId } });
      setGames(prev => prev.map(g => g.id === gameId ? updated : g));
      toast.success('Sparing anulowany');
    } catch (err) {
      toast.error(err?.body?.message || 'Nie udało się anulować sparingu');
    } finally { setCancellingId(null); }
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'APPROVED' || status === 'APPROVED_WITH_TIME_TO_CONFIRM') return 'admin-badge--active';
    if (status === 'CANCELLED') return 'admin-badge--cancelled';
    if (status === 'REJECTED') return 'admin-badge--expired';
    return 'admin-badge--pending';
  };

  return (
    <>
      <div className="admin-search-bar">
        <input type="text" className="admin-search-input" placeholder="Szukaj po nazwie drużyny/klubu..."
          value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
        <select className="admin-status-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button className="admin-search-btn" onClick={handleSearch} disabled={loading}>{loading ? 'Szukam...' : 'Szukaj'}</button>
      </div>
      {searched && !loading && games.length === 0 && <p className="admin-no-results">Brak wyników</p>}
      {games.length > 0 && (
        <div className="admin-teams-list">
          {games.map(game => (
            <div key={game.id} className="admin-team-item">
              <div className="admin-team-info">
                <span className="admin-team-name">
                  {game.creatorTeamName || '?'} vs {game.opponentTeamName || '?'}
                </span>
                <span className="admin-team-meta">
                  {game.date || 'brak daty'}
                  {game.startTime && <> &middot; {game.startTime.substring(0, 5)}</>}
                  {game.endTime && <>-{game.endTime.substring(0, 5)}</>}
                  {game.location && <> &middot; {game.location}</>}
                  {game.creatorSportsClubName && <> &middot; {game.creatorSportsClubName}</>}
                </span>
              </div>
              <div className="admin-game-actions">
                <span className={`admin-badge ${getStatusBadgeClass(game.status)}`}>
                  {statusLabels[game.status] || game.status}
                </span>
                {game.status !== 'CANCELLED' && game.status !== 'REJECTED' && (
                  <button className="admin-cancel-game-btn"
                    onClick={() => handleCancel(game.id)}
                    disabled={cancellingId === game.id}>
                    {cancellingId === game.id ? '...' : 'Anuluj'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ========== Zaproszenia (linki sparingowe) ==========
function InvitationsTab() {
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const formatDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  };

  const buildBulkText = (terms, url) => {
    const list = (terms || []).map(t => `${formatDate(t.startDate)} - ${formatDate(t.endDate)}`).join(', ');
    return `Drużyna z Twojej okolicy szuka przeciwników na poniższe terminy: ${list}. Zarejestruj się aby umówić sparing: ${url}`;
  };

  const handleSearch = async () => {
    setLoading(true); setSearched(true);
    try {
      const params = {};
      if (query) params.query = query;
      if (activeFilter !== '') params.active = activeFilter === 'true';
      const result = await __request(OpenAPI, { method: 'GET', url: '/admin/preseason-game-invitations', query: params });
      setInvitations(result);
    } catch { toast.error('Nie udało się pobrać zaproszeń'); }
    finally { setLoading(false); }
  };

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label + ' skopiowano');
    } catch {
      toast.error('Nie udało się skopiować');
    }
  };

  return (
    <>
      <div className="admin-search-bar">
        <input type="text" className="admin-search-input" placeholder="Szukaj po nazwie drużyny lub klubu..."
          value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
        <select className="admin-status-select" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
          <option value="">Wszystkie</option>
          <option value="true">Aktywne</option>
          <option value="false">Dezaktywowane</option>
        </select>
        <button className="admin-search-btn" onClick={handleSearch} disabled={loading}>{loading ? 'Szukam...' : 'Szukaj'}</button>
      </div>
      {searched && !loading && invitations.length === 0 && <p className="admin-no-results">Brak wyników</p>}
      {invitations.length > 0 && (
        <div className="admin-teams-list">
          {invitations.map(inv => (
            <div key={inv.id} className="admin-team-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div className="admin-team-info">
                  <span className="admin-team-name">
                    {inv.teamName || '?'}
                    {inv.sportsClubName && <> &middot; {inv.sportsClubName}</>}
                  </span>
                  <span className="admin-team-meta">
                    {inv.terms?.length || 0} {inv.terms?.length === 1 ? 'termin' : 'terminów'}
                    &middot; Odpowiedzi: {inv.responseCount}
                    {inv.createdAt && <> &middot; Utworzono: {new Date(inv.createdAt).toLocaleDateString('pl-PL')}</>}
                    {inv.expiresAt && <> &middot; Wygasa: {new Date(inv.expiresAt).toLocaleDateString('pl-PL')}</>}
                  </span>
                  <span className="admin-team-meta" style={{ color: '#555' }}>
                    {(inv.terms || []).map(t => `${formatDate(t.startDate)}-${formatDate(t.endDate)}`).join('; ')}
                  </span>
                </div>
                <span className={`admin-badge ${inv.active ? 'admin-badge--active' : 'admin-badge--expired'}`}>
                  {inv.active ? 'aktywny' : 'dezaktywowany'}
                </span>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Link:</label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="text" readOnly value={inv.shareUrl || ''}
                    style={{ flex: 1, padding: 6, fontSize: 12 }}
                    onFocus={(e) => e.target.select()} />
                  <button className="admin-search-btn" style={{ padding: '4px 10px', fontSize: 12 }}
                    onClick={() => handleCopy(inv.shareUrl, 'Link')}>Kopiuj</button>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Treść zaproszenia:</label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <textarea readOnly rows={4} value={buildBulkText(inv.terms, inv.shareUrl)}
                    style={{ flex: 1, padding: 6, fontSize: 12, lineHeight: 1.4, resize: 'vertical' }}
                    onFocus={(e) => e.target.select()} />
                  <button className="admin-search-btn" style={{ padding: '4px 10px', fontSize: 12 }}
                    onClick={() => handleCopy(buildBulkText(inv.terms, inv.shareUrl), 'Treść')}>Kopiuj</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ========== Emaile ==========
function EmailsTab() {
  const [activationEmail, setActivationEmail] = useState('');
  const [activationLoading, setActivationLoading] = useState(false);

  const [coachEmail, setCoachEmail] = useState('');
  const [clubName, setClubName] = useState('');
  const [coachLoading, setCoachLoading] = useState(false);

  const handleResendActivation = async (e) => {
    e.preventDefault();
    if (!activationEmail.trim()) return;
    setActivationLoading(true);
    try {
      const result = await __request(OpenAPI, {
        method: 'POST',
        url: '/admin/resend-activation-email',
        body: { email: activationEmail.trim() },
        mediaType: 'application/json',
      });
      toast.success(result.message || 'Email aktywacyjny wysłany');
      setActivationEmail('');
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Nie udało się wysłać emaila';
      toast.error(msg);
    } finally {
      setActivationLoading(false);
    }
  };

  const handleSendCoachInvitation = async (e) => {
    e.preventDefault();
    if (!coachEmail.trim() || !clubName.trim()) return;
    setCoachLoading(true);
    try {
      const result = await __request(OpenAPI, {
        method: 'POST',
        url: '/admin/send-coach-invitation',
        body: { email: coachEmail.trim(), sportsClubName: clubName.trim() },
        mediaType: 'application/json',
      });
      toast.success(result.message || 'Zaproszenie wysłane');
      setCoachEmail('');
      setClubName('');
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Nie udało się wysłać zaproszenia';
      toast.error(msg);
    } finally {
      setCoachLoading(false);
    }
  };

  return (
    <div className="admin-emails-tab">
      <div className="admin-email-section">
        <h3 className="admin-email-section-title">Ponowne wysłanie emaila aktywacyjnego</h3>
        <p className="admin-email-section-desc">
          Wyślij ponownie email z kodem aktywacyjnym do klubu sportowego. Email musi zgadzać się z adresem podanym przy rejestracji.
        </p>
        <form onSubmit={handleResendActivation} className="admin-email-form">
          <input
            type="email"
            placeholder="Email klubu sportowego"
            value={activationEmail}
            onChange={e => setActivationEmail(e.target.value)}
            className="admin-search-input"
            required
          />
          <button type="submit" className="admin-search-btn" disabled={activationLoading || !activationEmail.trim()}>
            {activationLoading ? 'Wysyłanie...' : 'Wyślij'}
          </button>
        </form>
      </div>

      <div className="admin-email-section">
        <h3 className="admin-email-section-title">Wysłanie zaproszenia dla trenera</h3>
        <p className="admin-email-section-desc">
          Wyślij email z zaproszeniem do rejestracji jako trener w podanym klubie.
        </p>
        <form onSubmit={handleSendCoachInvitation} className="admin-email-form">
          <input
            type="email"
            placeholder="Email trenera"
            value={coachEmail}
            onChange={e => setCoachEmail(e.target.value)}
            className="admin-search-input"
            required
          />
          <input
            type="text"
            placeholder="Nazwa klubu sportowego"
            value={clubName}
            onChange={e => setClubName(e.target.value)}
            className="admin-search-input"
            required
          />
          <button type="submit" className="admin-search-btn" disabled={coachLoading || !coachEmail.trim() || !clubName.trim()}>
            {coachLoading ? 'Wysyłanie...' : 'Wyślij zaproszenie'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AdminAccess;
